<template>
  <!-- processing update view -->
  <v-card
    flat
    class="py-5 px-1 px-md-5 d-flex flex-column justify-center align-center"
    v-if="processing"
  >
    <v-card-title>
      Processing
      <PulseLoader />
    </v-card-title>
    <BasicSpinner />
  </v-card>
  <!-- normal view -->
  <v-card flat class="py-5 px-1 px-md-5" v-else>
    <v-card v-if="user" flat class="pa-5 rounded-lg mb-5 bg-background">
      <v-card-text class="d-flex pb-0">
        <v-row
          no-gutters
          :align="mdAndUp ? 'center' : 'start'"
          class="flex-column flex-md-row"
        >
          <span class="mr-3">Profile picture</span>
          <v-avatar class="mr-3">
            <img v-if="avatarSrc" :src="avatarSrc" width="100%" height="auto" />
            <v-icon v-else icon="custom:avatar" size="100%" />
          </v-avatar>
          <v-spacer v-if="!showFileInput" />
          <!-- temporarily disabled. TODO: re-enable when file upload solution has been created -->
          <CustomTextButton
            v-if="!showFileInput"
            @click="showFileInput = true"
            disabled
          >
            Change picture
          </CustomTextButton>
          <v-file-input
            v-if="showFileInput"
            accept="image/*"
            label="Upload a new profile image"
            dense
            class="pt-4"
            v-model="avatar"
            prepend-icon="mdi-camera"
            :style="mdAndUp ? '' : 'width: 100%'"
          ></v-file-input>
        </v-row>
      </v-card-text>
      <v-card-text>
        <v-row no-gutters class="flex-column flex-sm-row">
          <v-text-field
            class="rounded-lg mr-sm-2"
            label="First name"
            v-model="firstName"
          ></v-text-field>
          <v-text-field
            class="rounded-lg ml-sm-2"
            label="Last name"
            v-model="lastName"
          ></v-text-field>
        </v-row>
        <v-text-field
          class="rounded-lg"
          label="Display name"
          v-model="displayName"
        ></v-text-field>
      </v-card-text>
    </v-card>
    <v-card-text v-if="user">
      <v-row>
        <v-spacer></v-spacer>
        <CustomOutlinedButton
          v-if="hasChanges"
          class="mt-n2 mr-2"
          @click="cancel"
        >
          Cancel
        </CustomOutlinedButton>
        <CustomSolidButton
          class="mt-n2"
          @click="saveAndUpload"
          :disabled="!hasChanges"
        >
          Save changes
        </CustomSolidButton>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script lang="ts" setup>
import type { UpdateUserDTO } from "@/api/vtnz-backend/user";
import CustomOutlinedButton from "@/components/ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue";
import CustomSolidButton from "@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import CustomTextButton from "@/components/ui-elements/buttons/custom-buttons/CustomTextButton.vue";
import BasicSpinner from "@/components/ui-elements/loaders/BasicSpinner.vue";
import PulseLoader from "@/components/ui-elements/loaders/PulseLoader.vue";
import { useUserStore } from "@/domains/user/userStore";
import { computed, nextTick, onMounted, ref } from "vue";
import { useDisplay } from "vuetify";

const userStore = useUserStore();
const { mdAndUp } = useDisplay();

const firstName = ref("");
const lastName = ref("");
const displayName = ref("")
const avatar = ref<File | null>(null);
const showFileInput = ref(false);

const user = computed(() => userStore.getCurrentUser);
const processing = ref(false);
const avatarSrc = computed(() => user.value?.avatarURL);
const hasChanges = computed(() => hasChanged());

function setInitialValues() {
  firstName.value = user.value?.firstName ?? "";
  lastName.value = user.value?.lastName ?? "";
  displayName.value = user.value?.displayName ?? "";
}

async function saveAndUpload() {
  // guard clause
  if (!user.value) return;
  if (!hasChanges.value) return;
  // check if data has changed from initial values and prep object for dispatch.
  const data: UpdateUserDTO = {
    first_name:
      firstName.value !== user.value.firstName ? firstName.value : undefined,
    last_name:
      lastName.value !== user.value.lastName ? lastName.value : undefined,
    display_name:
      displayName.value !== user.value.displayName ? displayName.value : undefined
  };
  processing.value = true;
  const res = await userStore.setAndUpdateUserDetails(data);
  if (!res.success) {
    // show some form of failure
    processing.value = false;
    return;
  }
  // show some form of success
  processing.value = false;
}

function hasChanged(): boolean {
  if (!user.value) return false; // no user to change no changes.

  return (
    firstName.value !== user.value?.firstName ||
    lastName.value !== user.value?.lastName ||
    displayName.value !== user.value?.displayName ||
    avatar.value !== null
  );
}

function cancel() {
  setInitialValues();
  showFileInput.value = false;
  avatar.value = null;
}

onMounted(() => {
  nextTick(() => {
    setInitialValues();
  });
});
</script>
